/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The two charges either side of today: the last one and the next one. */

import type { InvoiceItem } from '@services/requests/housien-services/AgenciesService.subscription';

/** A charge the block states: when it happened (or will), and how much. */
export interface Charge {
  /** Null when the amount is known but the date is not. */
  date: string | null;
  cents: number;
  /**
   * How many seats the amount covers.
   *
   * Absent when the caller cannot say. An invoice carries no seat count, so
   * the figure is the subscription's and has to be handed in from outside --
   * and a caller that does not hand one in leaves this undefined rather than
   * guessing, because the one thing read off it is whether the team changed.
   */
  seats?: number;
}

/**
 * The most recent charge the agency actually paid.
 *
 * Paid only. A pending invoice has not left anyone's account and an overdue
 * one is a problem to be stated elsewhere -- naming either as "last charge"
 * would tell the reader money moved when it did not.
 *
 * The list arrives newest first, but it is sorted here rather than trusted:
 * the server's order is not part of this screen's contract, and reading the
 * wrong end would quietly show a charge from a year ago.
 *
 * @param invoices - The invoices the page loaded.
 * @param seats - Seats the subscription holds today, when the caller knows.
 * @returns The last settled charge, or null when none has settled.
 */
export function lastCharge(
  invoices: InvoiceItem[] | null | undefined, seats?: number,
): Charge | null {
  const paid = (invoices ?? []).filter((item) => item.status === 'paid' && item.date);
  if (!paid.length) return null;
  const latest = paid.reduce((best, item) => (item.date > best.date ? item : best));
  return {
    date: latest.date,
    // What left the account, not what was billed: a partially settled invoice
    // is the one case where the two differ, and the reader cares about the
    // money that moved.
    cents: latest.amount_paid_cents ?? latest.amount_cents,
    seats,
  };
}

/**
 * The charge coming next, with any scheduled reduction already in it.
 *
 * A reduction applies at renewal, so between scheduling it and that date the
 * contracted seats and the seats being paid for next time disagree. Showing
 * today's total would promise a bill the agency is not going to get.
 *
 * A missing date does not hide the amount. The renewal date comes from the
 * payment broker and is absent in environments that have none; the figure is
 * computed here from seats and prices and is right either way. Withholding it
 * because the calendar is unknown answers "how much" with "we cannot say",
 * which is the one thing the reader came for.
 *
 * A coupon that takes the bill to zero is still a charge. Zero and "nothing
 * to charge for" print the same way through an amount alone, so the caller
 * says which one it is: a subscription whose seats price at nothing is a
 * subscription with no renewal to quote, and a free cycle is R$ 0,00 on a
 * date the agency can read.
 *
 * @param date - The renewal date, when the broker has given one.
 * @param cents - The renewal total, already computed against the schedule.
 * @param seats - Seats the renewal will cover, already counting the schedule.
 * @param billable - Whether there is a renewal to quote at all. Defaults to
 *   the amount being non-zero, which is right whenever no coupon is involved.
 * @returns The next charge, or null when there is nothing to charge for.
 */
export function nextCharge(
  date: string | null | undefined,
  cents: number,
  seats?: number,
  billable: boolean = cents > 0,
): Charge | null {
  if (!billable) return null;
  return { date: date ?? null, cents, seats };
}

/**
 * Whether the team is a different size at the next charge than at the last.
 *
 * The block says the amount moved "porque a equipe mudou de tamanho", and
 * that sentence is only true when the team did. It reads the seat counts and
 * not the amounts, because an amount moves for reasons that have nothing to
 * do with the team: a one-off coupon falling off is the common one, and a
 * solo broker who has exactly one seat and never had another was being told
 * their team had changed size.
 *
 * Unknown seats are not a change. A caller that cannot say how many seats a
 * charge covered leaves the count off, and silence is the honest reading --
 * asserting a team change from a figure nobody supplied is how this block
 * came to misinform the one agency that cannot have one.
 *
 * @param last - The last settled charge.
 * @param next - The charge coming.
 * @returns Whether the seat counts are both known and differ.
 */
export function seatsChanged(last: Charge | null, next: Charge | null): boolean {
  if (!last || !next) return false;
  if (last.seats === undefined || next.seats === undefined) return false;
  return last.seats !== next.seats;
}
